import { QueryClient } from '@tanstack/react-query';
import { Time } from '@gov-nx/core/types/time';

const MAX_RETRIES = 3;

/**
 * 4xx responses that are worth sending again. Everything else in the 4xx range
 * is a client error - the exact same request will keep failing, so retrying it
 * only delays the error the user is waiting for.
 */
const RETRYABLE_CLIENT_ERRORS = [
	408, // Request Timeout
	429, // Too Many Requests
];

const getResponseStatus = (error: unknown): number | undefined => {
	if (typeof error !== 'object' || error === null || !('response' in error)) {
		return undefined;
	}
	const { response } = error as { response?: { status?: unknown } };
	return typeof response?.status === 'number' ? response.status : undefined;
};

/**
 * React Query retries every failed query three times by default, regardless of
 * why it failed. That turned a plain 400 into a ~24s wait before the error
 * surfaced, and delayed the 401 handling that logs the user out.
 *
 * Requests that failed without a response (network errors, timeouts) and server
 * errors are still retried - those can succeed on a second attempt.
 */
export const shouldRetryRequest = (
	failureCount: number,
	error: unknown
): boolean => {
	if (failureCount >= MAX_RETRIES) {
		return false;
	}

	const status = getResponseStatus(error);

	if (status === undefined) {
		return true;
	}

	if (status >= 400 && status < 500) {
		return RETRYABLE_CLIENT_ERRORS.includes(status);
	}

	return true;
};

export const getQueryClient = () =>
	new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: Time['5 minutes'],
				retry: shouldRetryRequest,
			},
		},
	});
